'use client'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useInView } from 'motion/react'
import { useRef } from 'react'
import styles from './Results.module.scss'

export type ResultBlockProps = {
  number: string
  prefix?: string
  suffix?: string
  bottomText: string
  color: string
  randomChars?: string
}

const ResultBlock = ({ number, prefix, suffix, bottomText, color, randomChars }: ResultBlockProps) => {
  const animationScope = useRef(null)
  const animated = useRef<HTMLDivElement>(null)
  const inView = useInView(animationScope, { amount: 0.1, once: true })
  const charsLength = randomChars!.length

  useGSAP(
    () => {
      if (animated.current && inView) {
        const tl = gsap.timeline({ paused: true })
        animated.current.childNodes.forEach((child, i) => {
          child.childNodes.forEach((c) => {
            gsap.set(c, { yPercent: 0 })
          })

          gsap.set(child.childNodes[charsLength], { yPercent: charsLength * 100 })

          tl.add(
            gsap.to(child.childNodes, {
              ease: 'none',
              yPercent: '-=' + charsLength * 100,
            }),
            0.03 * i
          )
        })

        gsap.to(tl, { progress: 1, duration: 1.3, ease: 'circ.out' })
      }
    },
    { scope: animationScope, dependencies: [inView] }
  )

  return (
    <div ref={animationScope} className={styles.block + ' ' + styles[color]}>
      <div ref={animated} className={styles.number}>
        {prefix && (
          <div className={styles.symbol}>
            {[...randomChars!].map((x, i) => (
              <div key={'symbol' + i}>{x}</div>
            ))}
            <div className={styles.target}>{prefix}</div>
          </div>
        )}

        {[...number].map((num, i) => (
          <div key={num + i} className={styles.letter}>
            {[...randomChars!].map((x, i) => (
              <div key={'number' + i}>{x}</div>
            ))}
            <div className={styles.target}>{num}</div>
          </div>
        ))}

        {suffix && (
          <div className={styles.symbol}>
            {[...randomChars!].map((x, i) => (
              <div key={'symbol' + i}>{x}</div>
            ))}
            <div className={styles.target}>{suffix}</div>
          </div>
        )}
      </div>
      <div className={styles['bottom-text']}>
        {bottomText.includes(':') ? (
          <>
            {bottomText.split(':')[0]}:
            <br />
            {bottomText.split(':')[1]}
          </>
        ) : (
          bottomText
        )}
      </div>
    </div>
  )
}

export default ResultBlock
